import { IconButton } from '@nrx/ui';
import { useState, type JSX } from 'react';

import { TESTIMONIALS } from './content.js';
import { Band, BandHeading } from './sections.js';

/**
 * Member testimonial carousel.
 *
 * Shows a window of quotes and pages through them. The marketing site uses a swipe carousel; this
 * keeps the same shape but stays keyboard- and screen-reader-operable: the two controls are real
 * buttons, the live region announces position, and every quote stays in the DOM so a screen reader
 * can read the set without driving the control.
 */
export function Testimonials(): JSX.Element {
  const quotes = TESTIMONIALS.quotes;
  const [index, setIndex] = useState(0);
  const last = quotes.length - 1;

  return (
    <Band>
      <div className="flex flex-col gap-8">
        <BandHeading
          lead={TESTIMONIALS.headingLead}
          accent={TESTIMONIALS.headingAccent}
          className="text-center"
        />
        <div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          aria-live="polite"
          aria-atomic="false"
        >
          {quotes.map((quote, i) => {
            // Three at a time on desktop; the window wraps so paging never dead-ends.
            const inWindow = [index, (index + 1) % quotes.length, (index + 2) % quotes.length];
            return (
              <figure
                key={quote.slice(0, 24)}
                className={`flex flex-col gap-3 rounded-[var(--radius)] p-6 ${
                  inWindow.includes(i) ? '' : 'hidden'
                }`}
                style={{ backgroundColor: 'var(--tint-mint)' }}
              >
                <img
                  src="/images/quote-mark.svg"
                  alt=""
                  aria-hidden="true"
                  className="size-6 opacity-70"
                  width={24}
                  height={24}
                />
                <blockquote className="nrx-prose text-[var(--ink)]">{quote}</blockquote>
                <img
                  src="/images/five-stars.svg"
                  alt="Rated 5 out of 5"
                  className="h-4 w-auto"
                  height={16}
                />
              </figure>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-3">
          <IconButton
            onClick={() => setIndex((i) => (i === 0 ? last : i - 1))}
            aria-label="Previous testimonials"
            className="nrx-card flex size-10 items-center justify-center rounded-full text-[var(--forest)]"
          >
            <span aria-hidden="true">‹</span>
          </IconButton>
          <span className="text-sm text-[var(--ink-soft)]">
            {index + 1} of {quotes.length}
          </span>
          <IconButton
            onClick={() => setIndex((i) => (i === last ? 0 : i + 1))}
            aria-label="Next testimonials"
            className="nrx-card flex size-10 items-center justify-center rounded-full text-[var(--forest)]"
          >
            <span aria-hidden="true">›</span>
          </IconButton>
        </div>
      </div>
    </Band>
  );
}
